/**
 * Tracking information, differences need to be patched when tracking source updates
 *
 * https://github.com/preactjs/signals/blob/main/packages/core/src/index.ts
 *
 * cd9efbb411527e031001177f66ba90a445a04a23
 * Aug 3, 2024
 */

import { eq, LongTaskMonitor } from '@rehaje/shared'

import { Abort } from './constants'
import { FatalCycleError } from './errors'

export const RUNNING = 1 << 0
export const NOTIFIED = 1 << 1
export const OUTDATED = 1 << 2
export const DISPOSED = 1 << 3
export const HAS_ERROR = 1 << 4
export const TRACKING = 1 << 5

export const CYCLE_LIMIT = 100
export const CYCLE_DETECT_THRESHOLD = 10

export let batchDepth = 0
export let batchedEffect: Effect | undefined
export let batchedPostEffect: PostEffect | undefined
export let batchEndBatchEffect: EndBatchEffect | undefined
export let batchIteration = 0
export let batchLongTaskMonitor: LongTaskMonitor | undefined
export let evalContext: Computed | Effect | undefined
export let globalVersion = 0
export let postEffectIteration = 0
export let postEffectLongTaskMonitor: LongTaskMonitor | undefined
export let postEffectVersion = 0
export let unbatchDepth = 0
export let untrackDepth = 0

export type DisposeFn = () => void

export type EffectFn = () => DisposeFn | void

export interface EndBatchEffect {
  (): void
  _nextEndBatchEffect?: EndBatchEffect
  _prevEndBatchEffect?: EndBatchEffect
}

export type OnErrorFn = (error: any, firstTime?: boolean) => void
export interface PostEffect {
  (value: any, oldValue?: any): void
  _disposed?: boolean
  _executed?: boolean
  _nextBatchedPostEffect?: PostEffect
  _oldValue?: any
  _onError?: OnErrorFn
  _value?: any
  _version?: number
}
interface Node {
  _nextSource?: Node
  _nextTarget?: Node
  _prevSource?: Node
  _prevTarget?: Node
  _rollbackNode?: Node
  _source: Signal
  _target: Computed | Effect
  _version: number
}

export class Signal<T = any> {
  _key?: string
  _node?: Node
  _targets?: Node
  _value?: any
  _version = 0

  get value(): T {
    const node = addDependency(this)
    if (node !== undefined) {
      node._version = this._version
    }
    return this._value
  }

  set value(value: T) {
    if (value !== this._value) {
      if (batchIteration > CYCLE_LIMIT) {
        throw new FatalCycleError(`${this._key} cycleMaxLimit`)
      } else if (batchIteration > CYCLE_DETECT_THRESHOLD) {
        batchLongTaskMonitor ??= new LongTaskMonitor()
        if (batchLongTaskMonitor.detect()) {
          throw new FatalCycleError(`${this._key} longTaskGrowthLimit`)
        }
      } else {
        batchLongTaskMonitor = undefined
      }
      this._value = value
      this._version++
      globalVersion++
      batchDepth++
      try {
        for (
          let node = this._targets;
          node !== undefined;
          node = node._nextTarget
        ) {
          node._target._notify()
        }
      } finally {
        endBatch()
      }
    }
  }

  constructor(value?: T, key?: string) {
    this._value = value
    this._key = key
  }

  _refresh(): boolean {
    return true
  }

  _subscribe(node: Node): void {
    if (this._targets !== node && node._prevTarget === undefined) {
      node._nextTarget = this._targets
      if (this._targets !== undefined) {
        this._targets._prevTarget = node
      }
      this._targets = node
    }
  }

  _unsubscribe(node: Node): void {
    if (this._targets !== undefined) {
      const prev = node._prevTarget
      const next = node._nextTarget
      if (prev !== undefined) {
        prev._nextTarget = next
        node._prevTarget = undefined
      }
      if (next !== undefined) {
        next._prevTarget = prev
        node._nextTarget = undefined
      }
      if (node === this._targets) {
        this._targets = next
      }
    }
  }

  peek(): T {
    const prevContext = evalContext
    evalContext = undefined
    try {
      return this.value
    } finally {
      evalContext = prevContext
    }
  }

  subscribe(fn: (value?: T) => void): DisposeFn {
    return effect(() => {
      const value = this.value
      const prevContext = evalContext
      evalContext = undefined
      try {
        fn(value)
      } finally {
        evalContext = prevContext
      }
    })
  }

  toJSON(): T {
    return this.value
  }

  toString(): string {
    return this.value + ''
  }

  valueOf(): T {
    return this.value
  }
}

export class Computed<T = any> extends Signal<T> {
  _eq = eq
  _flags = OUTDATED
  _fn: () => T
  _globalVersion = globalVersion - 1
  _sources?: Node

  override get value(): T {
    if (this._flags & RUNNING) {
      throw new FatalCycleError(this._key)
    }
    const node = addDependency(this)
    this._refresh()
    if (node !== undefined) {
      node._version = this._version
    }
    if (this._flags & HAS_ERROR) {
      throw this._value
    }
    return this._value
  }

  constructor(fn: () => T, key?: string) {
    super(undefined, key)
    this._fn = fn
  }

  _notify(): void {
    if (!(this._flags & NOTIFIED)) {
      this._flags |= OUTDATED | NOTIFIED
      for (
        let node = this._targets;
        node !== undefined;
        node = node._nextTarget
      ) {
        node._target._notify()
      }
    }
  }

  override _refresh(): boolean {
    this._flags &= ~NOTIFIED
    if (this._flags & RUNNING) {
      return false
    }
    if ((this._flags & (OUTDATED | TRACKING)) === TRACKING) {
      return true
    }
    this._flags &= ~OUTDATED
    if (this._globalVersion === globalVersion) {
      return true
    }
    this._globalVersion = globalVersion
    this._flags |= RUNNING
    if (this._version > 0 && !needsToRecompute(this)) {
      this._flags &= ~RUNNING
      return true
    }
    const prevContext = evalContext
    try {
      prepareSources(this)
      evalContext = this
      const value = this._fn()
      if (
        this._flags & HAS_ERROR ||
        this._version === 0 ||
        !this._eq(value, this._value)
      ) {
        this._value = value
        this._flags &= ~HAS_ERROR
        this._version++
      }
    } catch (error) {
      this._value = error
      this._flags |= HAS_ERROR
      this._version++
    }
    evalContext = prevContext
    cleanupSources(this)
    this._flags &= ~RUNNING
    return true
  }

  override _subscribe(node: Node): void {
    if (this._targets === undefined) {
      this._flags |= OUTDATED | TRACKING
      for (
        let node = this._sources;
        node !== undefined;
        node = node._nextSource
      ) {
        node._source._subscribe(node)
      }
    }
    super._subscribe(node)
  }

  override _unsubscribe(node: Node): void {
    if (this._targets !== undefined) {
      super._unsubscribe(node)
      if (this._targets === undefined) {
        this._flags &= ~TRACKING
        for (
          let node = this._sources;
          node !== undefined;
          node = node._nextSource
        ) {
          node._source._unsubscribe(node)
        }
      }
    }
  }
}

export class Effect {
  _cleanup?: DisposeFn
  _flags = TRACKING
  _fn?: EffectFn
  _nextBatchedEffect?: Effect
  _onError?: OnErrorFn
  _sources?: Node
  _version = 0

  constructor(fn: EffectFn) {
    this._fn = fn
  }

  _callback(): void {
    const finish = this._start()
    try {
      if (this._flags & DISPOSED) return
      if (this._fn === undefined) return
      const cleanup = this._fn()
      this._version++
      if (typeof cleanup === 'function') {
        this._cleanup = cleanup
      }
    } catch (error) {
      if (error !== Abort) {
        if (this._onError === undefined) {
          throw error
        } else {
          this._onError(error, this._version === 0)
        }
      }
    } finally {
      finish()
    }
  }

  _dispose(): void {
    this._flags |= DISPOSED
    if (!(this._flags & RUNNING)) {
      disposeEffect(this)
    }
  }

  _notify(): void {
    if (!(this._flags & NOTIFIED)) {
      this._flags |= NOTIFIED
      this._nextBatchedEffect = batchedEffect
      batchedEffect = this
    }
  }

  _start(): DisposeFn {
    if (this._flags & RUNNING) {
      throw new FatalCycleError()
    }
    this._flags |= RUNNING
    this._flags &= ~DISPOSED
    cleanupEffect(this)
    prepareSources(this)
    batchDepth++
    const prevContext = evalContext
    evalContext = this
    return endEffect.bind(this, prevContext)
  }
}

export function action<T>(fn: () => T): T {
  let batched, untracked
  if (
    (batched = batchDepth > 0 || unbatchDepth > 0) &&
    (untracked = evalContext === undefined && untrackDepth > 0)
  ) {
    return fn()
  } else if (batched) {
    const prevContext = evalContext
    evalContext = undefined
    untrackDepth++
    try {
      return fn()
    } finally {
      untrackDepth--
      evalContext = prevContext
    }
  } else if (untracked) {
    batchDepth++
    try {
      return fn()
    } finally {
      endBatch()
    }
  } else {
    const prevContext = evalContext
    evalContext = undefined
    untrackDepth++
    batchDepth++
    try {
      return fn()
    } finally {
      endBatch()
      untrackDepth--
      evalContext = prevContext
    }
  }
}

export function actionBound<T extends (...args: any[]) => any>(fn: T): T {
  return function actionBound(this: any, ...args: any[]) {
    let batched, untracked
    if (
      (batched = batchDepth > 0 || unbatchDepth > 0) &&
      (untracked = evalContext === undefined && untrackDepth > 0)
    ) {
      return fn.apply(this, args)
    } else if (batched) {
      const prevContext = evalContext
      evalContext = undefined
      untrackDepth++
      try {
        return fn.apply(this, args)
      } finally {
        untrackDepth--
        evalContext = prevContext
      }
    } else if (untracked) {
      batchDepth++
      try {
        return fn.apply(this, args)
      } finally {
        endBatch()
      }
    } else {
      const prevContext = evalContext
      evalContext = undefined
      untrackDepth++
      batchDepth++
      try {
        return fn.apply(this, args)
      } finally {
        endBatch()
        untrackDepth--
        evalContext = prevContext
      }
    }
  } as T
}

export function addEndBatchEffect(endBatchEffect: EndBatchEffect): DisposeFn {
  if (batchEndBatchEffect !== undefined) {
    batchEndBatchEffect._prevEndBatchEffect = endBatchEffect
  }
  endBatchEffect._nextEndBatchEffect = batchEndBatchEffect
  batchEndBatchEffect = endBatchEffect
  return () => {
    const prev = endBatchEffect._prevEndBatchEffect
    const next = endBatchEffect._nextEndBatchEffect
    if (prev !== undefined) {
      prev._nextEndBatchEffect = next
    }
    if (next !== undefined) {
      next._prevEndBatchEffect = prev
    }
  }
}

export function addPostEffect(postEffect: PostEffect): void {
  if (!postEffect._disposed && postEffect._version !== postEffectVersion) {
    postEffect._version = postEffectVersion
    postEffect._nextBatchedPostEffect = batchedPostEffect
    batchedPostEffect = postEffect
  }
}
export function batch<T>(fn: () => T): T

export function batch<T>(fn: () => T) {
  if (batchDepth > 0 || unbatchDepth > 0) {
    return fn()
  }
  batchDepth++
  try {
    return fn()
  } catch (error) {
    if (error !== Abort) {
      throw error
    }
  } finally {
    endBatch()
  }
}

export function batchBound<T extends (...args: any[]) => any>(fn: T): T {
  return function batchBound(this: any, ...args: any[]) {
    if (batchDepth > 0 || unbatchDepth > 0) {
      return fn.apply(this, args)
    }
    batchDepth++
    try {
      return fn.apply(this, args)
    } catch (error) {
      if (error !== Abort) {
        throw error
      }
    } finally {
      endBatch()
    }
  } as T
}

/**
 * @deprecated
 * only for unit testing
 */
export function computed<T>(fn: () => T): Computed<T> {
  return new Computed<T>(fn)
}

/**
 * @deprecated
 * only for unit testing
 */
export function effect(fn: EffectFn): DisposeFn

export function effect(fn: Function): Function

export function effect(fn: any) {
  const effect = new Effect(fn)
  try {
    effect._callback()
  } catch (error) {
    effect._dispose()
    throw error
  }
  return effect._dispose.bind(effect)
}

export function signal<T>(value: T, key?: string): Signal<T>

export function signal<T = undefined>(): Signal<T | undefined>

export function signal<T>(value?: T, key?: string): Signal<T> {
  return new Signal(value, key)
}

export function suspendUntrack<T>(fn: () => T): T {
  if (untrackDepth === 0) {
    return fn()
  }
  const prevUntrackDepth = untrackDepth
  untrackDepth = 0
  try {
    return fn()
  } finally {
    untrackDepth = prevUntrackDepth
  }
}
export function unbatch<T>(fn: () => T): T {
  if (unbatchDepth > 0) {
    return fn()
  }
  unbatchDepth++
  try {
    return fn()
  } finally {
    unbatchDepth--
  }
}
export function untrack<T>(fn: () => T): T {
  if (evalContext === undefined && untrackDepth > 0) {
    return fn()
  }
  const prevContext = evalContext
  evalContext = undefined
  untrackDepth++
  try {
    return fn()
  } finally {
    untrackDepth--
    evalContext = prevContext
  }
}

export function untrackBound<T extends (...args: any[]) => any>(fn: T): T {
  return function untrackBound(this: any, ...args: any[]) {
    if (evalContext === undefined && untrackDepth > 0) {
      return fn.apply(this, args)
    }
    const prevContext = evalContext
    evalContext = undefined
    untrackDepth++
    try {
      return fn.apply(this, args)
    } finally {
      untrackDepth--
      evalContext = prevContext
    }
  } as T
}

function addDependency(signal: Signal): Node | undefined {
  if (evalContext === undefined) {
    return undefined
  }
  let node = signal._node
  if (node === undefined || node._target !== evalContext) {
    node = {
      _nextSource: undefined,
      _nextTarget: undefined,
      _prevSource: evalContext._sources,
      _prevTarget: undefined,
      _rollbackNode: node,
      _source: signal,
      _target: evalContext,
      _version: 0
    }
    if (evalContext._sources !== undefined) {
      evalContext._sources._nextSource = node
    }
    evalContext._sources = node
    signal._node = node
    if (evalContext._flags & TRACKING) {
      signal._subscribe(node)
    }
    return node
  } else if (node._version === -1) {
    node._version = 0
    if (node._nextSource !== undefined) {
      node._nextSource._prevSource = node._prevSource
      if (node._prevSource !== undefined) {
        node._prevSource._nextSource = node._nextSource
      }
      node._prevSource = evalContext._sources
      node._nextSource = undefined
      evalContext._sources!._nextSource = node
      evalContext._sources = node
    }
    return node
  }
  return undefined
}

function cleanupEffect(effect: Effect): void {
  const cleanup = effect._cleanup
  effect._cleanup = undefined
  if (typeof cleanup === 'function') {
    batchDepth++
    const prevContext = evalContext
    evalContext = undefined
    try {
      cleanup()
    } catch (error) {
      effect._flags &= ~RUNNING
      effect._flags |= DISPOSED
      disposeEffect(effect)
      throw error
    } finally {
      evalContext = prevContext
      endBatch()
    }
  }
}

function cleanupPostEffects(postEffect?: PostEffect, error?: any): boolean {
  let hasError = error !== undefined
  while (postEffect !== undefined) {
    if (error !== undefined && postEffect._onError !== undefined) {
      postEffect._onError(error)
      hasError = false
    }
    const next = postEffect._nextBatchedPostEffect
    postEffect._nextBatchedPostEffect = undefined
    postEffect = next
  }
  return hasError
}

function cleanupSources(target: Computed | Effect): void {
  let node = target._sources
  let head
  while (node !== undefined) {
    const prev = node._prevSource
    if (node._version === -1) {
      node._source._unsubscribe(node)
      if (prev !== undefined) {
        prev._nextSource = node._nextSource
      }
      if (node._nextSource !== undefined) {
        node._nextSource._prevSource = prev
      }
    } else {
      head = node
    }
    node._source._node = node._rollbackNode
    if (node._rollbackNode !== undefined) {
      node._rollbackNode = undefined
    }
    node = prev
  }
  target._sources = head
}

function disposeEffect(effect: Effect): void {
  for (
    let node = effect._sources;
    node !== undefined;
    node = node._nextSource
  ) {
    node._source._unsubscribe(node)
  }
  effect._fn = undefined
  effect._sources = undefined
  cleanupEffect(effect)
}

function endBatch(): void {
  if (batchDepth > 1) {
    batchDepth--
    return
  }
  let error: any
  let hasError = false
  while (batchedEffect !== undefined) {
    let effect: Effect | undefined = batchedEffect
    batchedEffect = undefined
    batchIteration++
    while (effect !== undefined) {
      const next: Effect | undefined = effect._nextBatchedEffect
      effect._nextBatchedEffect = undefined
      effect._flags &= ~NOTIFIED
      if (!(effect._flags & DISPOSED) && needsToRecompute(effect)) {
        try {
          effect._callback()
        } catch (error_) {
          if (!hasError && error_ !== Abort) {
            error = error_
            hasError = true
          }
        }
      }
      effect = next
    }
  }
  batchIteration = 0
  batchDepth--
  if (batchEndBatchEffect !== undefined) {
    untrack(runEndBatchEffects)
  }
  if (batchedPostEffect !== undefined) {
    const iteration = postEffectIteration++
    try {
      let cycleError: FatalCycleError | undefined
      if (iteration > CYCLE_LIMIT) {
        cycleError = new FatalCycleError('postEffect cycleMaxLimit')
      } else if (iteration > CYCLE_DETECT_THRESHOLD) {
        postEffectLongTaskMonitor ??= new LongTaskMonitor()
        if (postEffectLongTaskMonitor.detect()) {
          cycleError = new FatalCycleError('postEffect longTaskGrowthLimit')
        }
      } else {
        postEffectLongTaskMonitor = undefined
      }
      if (cycleError) {
        const hasError = cleanupPostEffects(batchedPostEffect, cycleError)
        batchedPostEffect = undefined
        if (hasError) {
          throw cycleError
        }
      }
      action(runPostEffects)
    } catch (error_) {
      if (iteration > 1) {
        throw error_
      }
      if (!hasError && error_ !== Abort) {
        error = error_
        hasError = true
      }
    }
    postEffectIteration = 0
  }
  if (hasError) {
    throw error
  }
}

function endEffect(this: Effect, prevContext?: Computed | Effect) {
  if (evalContext !== this) {
    throw new FatalCycleError('Out-of-order effect')
  }
  cleanupSources(this)
  evalContext = prevContext
  this._flags &= ~RUNNING
  if (this._flags & DISPOSED) {
    disposeEffect(this)
  }
  endBatch()
}

function needsToRecompute(target: Computed | Effect): boolean {
  for (
    let node = target._sources;
    node !== undefined;
    node = node._nextSource
  ) {
    if (
      node._source._version !== node._version ||
      !node._source._refresh() ||
      node._source._version !== node._version
    ) {
      return true
    }
  }
  return false
}

function prepareSources(target: Computed | Effect): void {
  for (
    let node = target._sources;
    node !== undefined;
    node = node._nextSource
  ) {
    const rollbackNode = node._source._node
    if (rollbackNode !== undefined) {
      node._rollbackNode = rollbackNode
    }
    node._source._node = node
    node._version = -1
    if (node._nextSource === undefined) {
      target._sources = node
      break
    }
  }
}
function runEndBatchEffects(): void {
  for (
    let endBatchEffect = batchEndBatchEffect;
    endBatchEffect !== undefined;
    endBatchEffect = endBatchEffect._nextEndBatchEffect
  ) {
    endBatchEffect()
  }
}
function runPostEffects(): void {
  while (batchedPostEffect !== undefined) {
    let postEffect = batchedPostEffect as PostEffect | undefined
    batchedPostEffect = undefined
    while (postEffect !== undefined) {
      const next = postEffect._nextBatchedPostEffect
      postEffect._nextBatchedPostEffect = undefined
      if (!postEffect._disposed && postEffect._executed) {
        try {
          postEffect(postEffect._value, postEffect._oldValue)
        } catch (error) {
          if (error !== Abort) {
            if (postEffect._onError !== undefined) {
              postEffect._onError(error)
            }
            cleanupPostEffects(next)
            throw error
          }
        }
      }
      postEffect._oldValue = postEffect._value
      postEffect._executed = true
      postEffect = next
    }
  }
  postEffectVersion++
}
